import { useId, useMemo, useState } from 'react';
import { Button } from '../../atoms/Button/Button';
import { Icon } from '../../atoms/Icon/Icon';
import { formatRange, isSameDay, type DateRange } from '../../molecules/CalendarMonth/calendar';
import { Popover, popoverAnchorClass } from '../../primitives/Popover/Popover';
import { DatePickerMenu, type DatePickerMenuType } from '../DatePickerMenu/DatePickerMenu';
import { defaultDatePickerPresets, type DatePickerPreset } from '../DatePickerMenu/presets';
import styles from './DatePicker.module.css';

/**
 * Figma: `Date picker dropdown` (123:2527) — "The top-level date picker
 * component combining a trigger button with the calendar menu. Manages
 * open/closed state, date type, and selection state. Variants: Opened (True,
 * False) × Type (Dual dates, Single date) × State (Default, Active) ×
 * Breakpoint (Desktop)."
 *
 * Only `Type` is a prop. `Opened` is the menu being open, `State` is whether
 * anything is selected — "Default — no dates selected, trigger shows a
 * placeholder label", "Active — dates have been applied, the trigger displays
 * the selection summary" — so both are derived, not passed. `Breakpoint` has a
 * single option and nothing to switch between.
 */
export type DatePickerType = DatePickerMenuType;

type DatePickerBaseProps = {
  /** Figma `Pre-set ranges` — the sidebar in dual mode, a `Select` in single. */
  presets?: DatePickerPreset[];
  showPresets?: boolean;
  /**
   * Figma `Actions`. With the footer on, the menu edits a draft and `Apply`
   * commits it — which is what makes `State=Active` mean "dates have been
   * applied". With it off, every click commits immediately.
   */
  showActions?: boolean;
  onApply?: () => void;
  onCancel?: () => void;
  /** Figma `Opened`. Omit to let the picker own it. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** The leftmost month the menu opens on. Defaults to the selection, else today. */
  defaultMonth?: Date;
  isDateDisabled?: (date: Date) => boolean;
  /** Figma `State=Default`: "Select dates" for a range, "Select date" for one. */
  placeholder?: string;
  disabled?: boolean;
  locale?: string;
  className?: string;
};

type DualProps = DatePickerBaseProps & {
  type?: 'dualDates';
  value?: DateRange;
  defaultValue?: DateRange;
  onValueChange?: (value: DateRange) => void;
};

type SingleProps = DatePickerBaseProps & {
  type: 'singleDate';
  value?: Date | null;
  defaultValue?: Date | null;
  onValueChange?: (value: Date | null) => void;
};

export type DatePickerProps = DualProps | SingleProps;

const EMPTY: DateRange = { start: null, end: null };

const asRange = (value: DateRange | Date | null | undefined): DateRange => {
  if (value === undefined || value === null) return EMPTY;
  return value instanceof Date ? { start: value, end: null } : value;
};

export function DatePicker(props: DatePickerProps) {
  const {
    type = 'dualDates',
    presets,
    showPresets = true,
    showActions = true,
    onApply,
    onCancel,
    open,
    defaultOpen = false,
    onOpenChange,
    defaultMonth,
    isDateDisabled,
    placeholder,
    disabled = false,
    locale,
    className,
  } = props;

  const menuId = useId();
  const single = type === 'singleDate';

  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const [uncontrolledValue, setUncontrolledValue] = useState<DateRange>(() =>
    asRange(props.defaultValue),
  );
  /*
   * What the menu is showing before Apply. `null` means "no edits yet", so the
   * trigger and the grid both fall back to the committed value.
   */
  const [draft, setDraft] = useState<DateRange | null>(null);

  const isOpen = (open ?? uncontrolledOpen) && !disabled;
  const committed = props.value !== undefined ? asRange(props.value) : uncontrolledValue;
  const shown = draft ?? committed;

  const setOpen = (next: boolean) => {
    if (next === isOpen) return;
    if (next) setDraft(null);
    if (open === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const commit = (next: DateRange) => {
    if (props.value === undefined) setUncontrolledValue(next);
    /*
     * The two `onValueChange` signatures cannot be called through the union, so
     * the branch that knows the shape does the call.
     */
    if (single) (props as SingleProps).onValueChange?.(next.start);
    else (props as DualProps).onValueChange?.(next);
  };

  const handleChange = (next: DateRange) => {
    if (showActions) setDraft(next);
    else commit(next);
  };

  const handleApply = () => {
    if (draft) commit(draft);
    setDraft(null);
    setOpen(false);
    onApply?.();
  };

  const handleCancel = () => {
    setDraft(null);
    setOpen(false);
    onCancel?.();
  };

  /*
   * A committed range that exactly matches a preset reads as that preset's
   * name — "This week" — rather than its raw dates, the same way the picker
   * behind most analytics dashboards (and Figma's own frozen "This week vs.
   * Previous week" frame) labels its trigger. Falls back to `defaultDatePickerPresets`
   * so this still works for a caller that lets `DatePickerMenu` supply its own
   * presets.
   */
  const fallbackPresets = useMemo(() => defaultDatePickerPresets(), []);
  const rows = presets ?? fallbackPresets;
  const matchingPreset = rows.find((row) => {
    const range = row.getValue();
    if (!range.start || !isSameDay(range.start, committed.start)) return false;
    return single || isSameDay(range.end, committed.end);
  });

  const fallback = placeholder ?? (single ? 'Select date' : 'Select dates');
  const label = matchingPreset?.label ?? formatRange(committed, locale) ?? fallback;

  return (
    <div className={[popoverAnchorClass, styles.root, className].filter(Boolean).join(' ')}>
      {/*
       * Figma `Opened=True` swaps the trigger to `tertiary-surface-hover`, which
       * is exactly the forced-hover state `Button` already exposes.
       *
       * `aria-haspopup` / `aria-expanded` / `aria-controls` are not in Figma —
       * Figma has no concept of a disclosure relationship.
       */}
      <Button
        hierarchy="tertiary"
        iconLeading={<Icon name="calendar" />}
        onClick={() => setOpen(!isOpen)}
        disabled={disabled}
        state={isOpen ? 'hover' : undefined}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
      >
        {label}
      </Button>

      <Popover
        open={isOpen}
        onClose={handleCancel}
        placement="bottom-end"
        surface={false}
        role="dialog"
        label={single ? 'Choose a date' : 'Choose a date range'}
        id={menuId}
      >
        <DatePickerMenu
          type={type}
          value={shown}
          onValueChange={handleChange}
          presets={presets}
          showPresets={showPresets}
          showActions={showActions}
          onApply={handleApply}
          onCancel={handleCancel}
          defaultMonth={defaultMonth}
          isDateDisabled={isDateDisabled}
          locale={locale}
        />
      </Popover>
    </div>
  );
}
